import React from 'react';
import Papa from 'papaparse';
import adminService from '../../../services/adminService';
import { useAuth } from '../../../contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

function useAsyncState(initial) {
  const [state, setState] = React.useState(initial);
  const set = React.useCallback((patch) => setState((prev) => ({ ...prev, ...patch })), []);
  return [state, set];
}

const Field = ({ name, value, onChange }) => {
  const isLong = typeof value === 'string' && value.length > 120;
  if (isLong) {
    return (
      <textarea
        className="w-full border rounded p-2 text-sm"
        rows={6}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      className="w-full border rounded p-2 text-sm"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default function ResourceManager({ config, resourceKey }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [s, set] = useAsyncState({
    items: [],
    loading: true,
    error: null,
    selected: null,
    showEditor: false,
    csvBusy: false,
    exportBusy: false,
    limit: 100,
    offset: 0,
    inferredColumns: []
  });

  const queryKey = React.useMemo(() => ['admin-table', config.table, s.limit, s.offset], [config.table, s.limit, s.offset]);

  const { data, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const items = await adminService.list(config.table, {
        order: config.listOrder,
        limit: s.limit,
        offset: s.offset
      });
      return items;
    },
    staleTime: 30 * 1000
  });

  React.useEffect(() => {
    const inferredColumns = data?.[0] ? Object.keys(data[0]) : (config.columnsHint || []);
    set({ items: data || [], inferredColumns, loading: isFetching, error: error ? (error.message || String(error)) : null });
  }, [data, isFetching, error, config.columnsHint, set]);

  const startNew = () => set({ selected: { ...(config.newTemplate || {}) }, showEditor: true });
  const startEdit = (row) => set({ selected: { ...row }, showEditor: true });
  const cancelEdit = () => set({ selected: null, showEditor: false });

  const invalidate = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const save = async () => {
    try {
      const rec = s.selected;
      // prefer upsert when onConflict is configured; otherwise create/update by id presence
      if (config.onConflict) {
        await adminService.upsert(config.table, rec, { onConflict: config.onConflict });
      } else if (rec?.[config.idColumn || 'id']) {
        await adminService.update(config.table, rec[config.idColumn || 'id'], rec, { idColumn: config.idColumn || 'id' });
      } else {
        await adminService.create(config.table, rec);
      }
      set({ showEditor: false, selected: null });
      invalidate();
    } catch (err) {
      alert('Save failed: ' + (err.message || String(err)));
    }
  };

  const remove = async (row) => {
    if (!confirm('Delete this record?')) return;
    try {
      await adminService.remove(config.table, row[config.idColumn || 'id'], { idColumn: config.idColumn || 'id' });
      invalidate();
    } catch (err) {
      alert('Delete failed: ' + (err.message || String(err)));
    }
  };

  const onCSV = async (file) => {
    if (!file) return;
    set({ csvBusy: true });
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = results.data;
          await adminService.bulkInsert(config.table, rows, { onConflict: config.onConflict });
          invalidate();
        } catch (err) {
          alert('CSV import failed: ' + (err.message || String(err)));
        } finally {
          set({ csvBusy: false });
        }
      },
      error: (err) => {
        alert('CSV parse error: ' + (err.message || String(err)));
        set({ csvBusy: false });
      }
    });
  };

  const exportCSV = () => {
    try {
      const csv = Papa.unparse({
        fields: s.inferredColumns,
        data: s.items.map((row) => s.inferredColumns.map((c) => {
          const v = row[c];
          if (v == null) return '';
          if (typeof v === 'object') return JSON.stringify(v);
          return String(v);
        }))
      });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.table}-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'_')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('CSV export failed: ' + (err.message || String(err)));
    }
  };

  const exportAll = async () => {
    try {
      set({ exportBusy: true });
      const allItems = await adminService.list(config.table, {
        order: config.listOrder,
        limit: 5000,
        offset: 0
      });
      const cols = allItems?.[0] ? Object.keys(allItems[0]) : (config.columnsHint || []);
      const csv = Papa.unparse({
        fields: cols,
        data: allItems.map((row) => cols.map((c) => {
          const v = row[c];
          if (v == null) return '';
          if (typeof v === 'object') return JSON.stringify(v);
          return String(v);
        }))
      });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.table}-all-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'_')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export all failed: ' + (err.message || String(err)));
    } finally {
      set({ exportBusy: false });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{config.title}</h2>
          <p className="text-sm text-gray-500">Table: {config.table}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-2 rounded bg-blue-600 text-white text-sm" onClick={startNew}>New</button>
          <label className="px-3 py-2 rounded bg-gray-100 border text-sm cursor-pointer">
            {s.csvBusy ? 'Importing…' : 'Upload CSV'}
            <input type="file" accept=".csv" className="hidden" onChange={(e) => onCSV(e.target.files?.[0])} />
          </label>
          <button className="px-3 py-2 rounded border text-sm" onClick={exportCSV}>Export (page)</button>
          <button className="px-3 py-2 rounded border text-sm" onClick={exportAll} disabled={s.exportBusy}>
            {s.exportBusy ? 'Exporting…' : 'Export all'}
          </button>
        </div>
      </div>

      {s.loading ? (
        <div className="p-4 text-gray-500">Loading…</div>
      ) : s.error ? (
        <div className="p-4 text-red-600">{s.error}</div>
      ) : (
        <>
          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {s.inferredColumns.map((col) => (
                    <th key={col} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">{col}</th>
                  ))}
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {s.items.map((row) => (
                  <tr key={row[config.idColumn || 'id']} className="border-t">
                    {s.inferredColumns.map((col) => (
                      <td key={col} className="px-3 py-2 align-top max-w-[360px]">
                        <div className="truncate" title={row[col] != null ? String(row[col]) : ''}>
                          {row[col] == null ? '' : typeof row[col] === 'object' ? JSON.stringify(row[col]) : String(row[col])}
                        </div>
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button className="px-2 py-1 text-blue-700" onClick={() => startEdit(row)}>Edit</button>
                      <button className="px-2 py-1 text-red-700" onClick={() => remove(row)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {s.items.length === 0 && (
                  <tr><td colSpan={s.inferredColumns.length + 1} className="px-3 py-6 text-center text-gray-500">No records</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>Showing {s.items.length} rows (page offset {s.offset})</div>
            <div className="flex gap-2">
              <button
                className="px-3 py-2 rounded border"
                onClick={() => set({ offset: Math.max(0, s.offset - s.limit) })}
                disabled={s.offset <= 0}
              >
                Prev
              </button>
              <button
                className="px-3 py-2 rounded border"
                onClick={() => set({ offset: s.offset + s.limit })}
                disabled={s.items.length < s.limit}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {s.showEditor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-xl w-full max-w-3xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">{s.selected?.[config.idColumn || 'id'] ? 'Edit' : 'Create'} {config.title}</h3>
              <button className="text-gray-600" onClick={cancelEdit}>Close</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(config.columnsHint || Object.keys(s.selected || {})).map((key) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs text-gray-500">{key}</label>
                  <Field
                    name={key}
                    value={s.selected?.[key]}
                    onChange={(val) => set({ selected: { ...s.selected, [key]: val } })}
                  />
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button className="px-3 py-2 rounded border" onClick={cancelEdit}>Cancel</button>
              <button className="px-3 py-2 rounded bg-blue-600 text-white" onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
